import { FormCheck, FormLabel } from 'react-bootstrap';
import { RadioGroup } from 'react-bootstrap-formutil';
import FormGroup, { FormGroupProps } from 'components/FormGroup';
import classlist from 'utils/classlist';
import './style.scss';

export interface RadioFormutilProps<T = string> extends FormGroupProps<T> {
    disabled?: boolean;
    name: string;
    label?: React.ReactNode;
    labelClassName?: string;
    inline?: boolean;
    reverse?: boolean;
    needUnderLine?: boolean; // 是否需要下面的下划线
    options: Array<{
        className?: string;
        disabled?: boolean; // 单独禁用某些选项
        value: string | number;
        label: React.ReactNode;
    }>;
}

/**
 * @description 封装好的, 包含FormGroup功能的RadioGroup组件.
 * 之后需要使用RadioGroup的地方, 都优先考虑使用这个组件.
 */
const RadioFormutil: <T>(
    props: React.PropsWithChildren<RadioFormutilProps<T>>
) => React.ReactElement<any, any> = props => {
    const {
        disabled,
        options,
        name,
        label,
        labelClassName,
        inline,
        reverse = true,
        needUnderLine = true,
        className,
        $parser: customerParser,
        $formatter: customerFormatter,
        ...restProps
    } = props;
    const isNumberOption = options.every(option => typeof option.value === 'number');

    return (
        <div className={classlist('multiple-radio-root', className)}>
            <FormGroup
                name={name}
                label={
                    label && (
                        <FormLabel className={classlist('d-block', labelClassName)} htmlFor={name}>
                            {label}
                        </FormLabel>
                    )
                }
                $parser={(value, setViewValue) => {
                    if (customerParser) {
                        return customerParser(value, setViewValue);
                    }

                    return isNumberOption ? Number(value) : value;
                }}
                $formatter={(value, setModelValue) => {
                    if (customerFormatter) {
                        return customerFormatter(value, setModelValue);
                    }

                    // 这里需要转换成String, 是为了方便checked的比较, 参考下方FormCheck的注释
                    return isNumberOption ? String(value) : value;
                }}
                {...restProps}>
                <RadioGroup>
                    {options.map(({ disabled: optionDisabled, className: formCheckClassName, value, label }) => {
                        return (
                            <FormCheck
                                key={value}
                                className={classlist('form-check-radio', formCheckClassName, {
                                    'radio-gap': !inline,
                                    'need-under-line': needUnderLine,
                                    'fs-6 fw-normal normal-text-color': !reverse,
                                    reverse
                                })}
                                disabled={disabled || optionDisabled}
                                id={`${name}_${value}`}
                                type="radio"
                                label={label}
                                inline={inline}
                                /**
                                 * 这里加一个String的比较是因为:
                                 * radio是否被checked, RadioGroup比较的是value(FromGroup的表单值, 也就是dom节点的值, dom节点只接收string作为属性)和当前FormCheck的value
                                 * RadioGroup源码: checked: value === childValue
                                 * 但是FromCheck这里经过了处理, value传的是什么类型, childValue拿到的就是什么类型
                                 * 所以当options定义的是一个number类型的数组时, 这里会存在表单值(也就是dom节点值)和是否选中匹配不上的问题
                                 * 因为上面我们只进行了$parser={value => Number(value)}和 $formatter={value => String(value)}的处理
                                 * 并没有使用$parser和$formatter的第二个参数二次处理表单值和模型值, 如果这么处理了, 因为RadioGroup定义了value的类型是string(使用propType定义的), 所以控制台会报错, 当然只有测试环境会, 生产环境会被忽略
                                 * 所以这里直接使用String(value)进行类型转换, 保证进行checked比较时候类型的一致性
                                 */
                                value={String(value)}
                            />
                        );
                    })}
                </RadioGroup>
            </FormGroup>
        </div>
    );
};

export default RadioFormutil;
